import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import isInLegend from "../lib/isInLegend";
import { color, fonts } from "../..";
import ShadowedScrollArea from "../../ShadowedScrollArea";

interface Period {
  from: number;
  to: number;
}

export interface P<T extends Highcharts.SeriesOptionsType> {
  chartRefs: React.RefObject<Highcharts.Chart>[];
  series: T[];
  onLegendItemClick?: (serieId: string, altClick: boolean) => void;
  period?: Period;
  renderItem?: (serie: T) => React.ReactElement;
  renderSymbol?: (series: T) => React.ReactNode;
  renderLabel?: (series: T) => React.ReactNode;
  groupBy?: (serie: T) => string | null;
  renderGroupHeader?: (group: string, series: T[]) => React.ReactNode;
  isGroupCollapsed?: (group: string) => boolean;
  groups?: string[];
}

const Wrapper = styled(ShadowedScrollArea)`
  height: 100%;
  width: 240px;
  display: flex;
  flex-direction: column;
`;

export const HIDDEN_COLOR = color.titanium200;
export const LegendItem = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  color: ${HIDDEN_COLOR};
  text-overflow: ellipsis;
  font: ${fonts.body2SemiBold};
  padding-left: 4px;

  &.selected {
    display: none;
  }

  &.visible {
    color: ${color.carbon800};
  }

  &.hover {
    color: ${color.carbon900};
    background-color: ${color.titanium100};

    &.visible {
      color: ${color.carbon900};
    }
  }
`;

export const SquareSymbol = styled.div<{ color: string }>`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  background-color: ${props => props.color};
`;

const LineSymbol = styled.div<{ color: string }>`
  flex-shrink: 0;
  width: 14px;
  height: 2px;
  background-color: ${props => props.color};
`;

export const Label = styled.div`
  flex: 1;
  min-width: 0px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 4px;
  padding-bottom: 4px;
`;

export function defaultRenderSymbol<T extends Highcharts.SeriesOptionsType>(
  serie: T,
): React.ReactNode {
  const color = serie.visible === false ? HIDDEN_COLOR : serie.color;
  if (typeof color !== "string") return null;

  switch (serie.type) {
    case "line":
      return <LineSymbol color={color} />;
    case "arearange":
    case "column":
    case "bubble":
      return <SquareSymbol color={color} />;
    default:
      return null;
  }
}

export function defaultRenderLabel<T extends Highcharts.SeriesOptionsType>(
  serie: T,
): React.ReactNode {
  return <Label>{serie.name}</Label>;
}

function Legend<T extends Highcharts.SeriesOptionsType>({
  chartRefs,
  series,
  period,
  renderSymbol = defaultRenderSymbol,
  renderLabel = defaultRenderLabel,
  renderItem,
  onLegendItemClick,
  groupBy,
  renderGroupHeader,
  isGroupCollapsed,
  groups,
}: P<T>) {
  const legendSeries = React.useMemo(
    () =>
      series
        .filter(s => isInLegend(s, period))
        .sort((s1, s2) => {
          const s1Index = s1.legendIndex ?? 0;
          const s2Index = s2.legendIndex ?? 0;

          if (s1Index < s2Index) return -1;
          if (s1Index > s2Index) return 1;
          return 0;
        }),
    [series, period],
  );

  const legendEntries = React.useMemo<
    (
      | { type: "header"; group: string; series: T[] }
      | { type: "item"; serie: T; group: string | null }
    )[]
  >(() => {
    if (!groupBy) {
      return legendSeries.map(serie => ({ type: "item" as const, serie, group: null }));
    }

    const grouped = new Map<string, T[]>();
    const ungrouped: { type: "item"; serie: T; group: string | null }[] = [];

    for (const serie of legendSeries) {
      const groupName = groupBy(serie);
      if (!groupName) {
        ungrouped.push({ type: "item", serie, group: null });
        continue;
      }
      const list = grouped.get(groupName) ?? [];
      list.push(serie);
      grouped.set(groupName, list);
    }

    const groupNames = groups ?? Array.from(grouped.keys());
    const entries: (
      | { type: "header"; group: string; series: T[] }
      | { type: "item"; serie: T; group: string | null }
    )[] = [...ungrouped];

    for (const groupName of groupNames) {
      const groupSeries = grouped.get(groupName) ?? [];
      entries.push({ type: "header", group: groupName, series: groupSeries });
      for (const serie of groupSeries) {
        entries.push({ type: "item", serie, group: groupName });
      }
    }

    return entries;
  }, [groupBy, groups, legendSeries]);

  const [hoveredSerieId, setHoveredSerieId] = React.useState<string | null>(null);

  const handleMouseEnter = React.useCallback(
    (serie: T) => {
      for (const ref of chartRefs) {
        const obsCharts = ref.current;
        if (!obsCharts) throw new Error("Bad obschart");

        const linkedSerie = obsCharts.series.find(s => s.options.id === serie.id);

        if (!linkedSerie) continue;

        if (linkedSerie.visible) {
          for (const s of obsCharts.series) {
            if (s.options.id !== serie.id) {
              s.setState("inactive");
            }
          }
        }
        linkedSerie.setState("hover");
        setHoveredSerieId(linkedSerie.options.id!);
      }
    },
    [chartRefs],
  );
  const handleMouseLeave = React.useCallback(() => {
    for (const ref of chartRefs) {
      const obsCharts = ref.current;
      if (!obsCharts) throw new Error("Bad obschart");

      for (const s of obsCharts.series) {
        s.setState();
      }
      setHoveredSerieId(null);
    }
  }, [chartRefs]);

  return (
    <Wrapper>
      {legendEntries.map(entry => {
        if (entry.type === "header") {
          return (
            <React.Fragment key={`${entry.type}-${entry.group}`}>
              {renderGroupHeader?.(entry.group, entry.series)}
            </React.Fragment>
          );
        }

        const { serie, group } = entry;
        if (group && isGroupCollapsed?.(group)) {
          return null;
        }
        const serieId = serie.id!;
        const props = {
          className: cn({
            visible: serie.visible,
            hover: hoveredSerieId === serieId,
            selected: serie.selected === undefined ? false : !serie.selected,
          }),
          onClick: (e: React.MouseEvent<HTMLDivElement>) => {
            onLegendItemClick?.(serie.id!, e.metaKey || e.altKey || e.shiftKey || e.ctrlKey);
          },
          onMouseEnter: () => {
            handleMouseEnter(serie);
          },
          onMouseLeave: handleMouseLeave,
        };

        if (renderItem) {
          const item = renderItem(serie);
          props.className = cn(props.className, item.props.className);

          return React.cloneElement(item, {
            key: serie.id,
            ...props,
          });
        }

        return (
          <LegendItem key={serie.id} {...props} title={serie.name}>
            {renderSymbol(serie)}
            {renderLabel(serie)}
          </LegendItem>
        );
      })}
    </Wrapper>
  );
}

export default Legend;
